<template>
	<div class="matchdays">
		<LoadingSpinner
			v-if="isLoading"
			class="matchdays__loading-spinner"
		/>
		<MobileFilter
			ref="mobileFilter"
			:filters="mobileFilters"
			@changeSelect="handleMobileFilterChange"
			@reset="handleMobileFilterChange('reset', 'all')"
		/>
		<template v-if="!isLoading">
			<div class="matchdays__filter">
				<div class="matchdays__filter-left" />
				<div class="matchdays__filter-select">
					<Select
						:options="categoryFilter.options"
						:value="categoryFilter.value"
						:searchable="false"
						:clearable="false"
						:dark-theme="!$arenaConfig.isLightTheme"
						:rounded="true"
						@change="handleCategoryChange"
					/>
					<Select
						:options="teamFilter.options"
						:value="teamFilter.value"
						:searchable="false"
						:clearable="false"
						:dark-theme="!$arenaConfig.isLightTheme"
						:rounded="true"
						@change="handleTeamChange"
					/>
				</div>
				<div
					class="matchdays__mobile-filter-toggle"
					:class="{'is-filtered': mobileFilterIsFiltered }"
					@click="openMobileFilter"
				>
					<Icon
						icon-name="filter"
						size="lg"
					/>
				</div>
			</div>
			<div class="matchdays__list-w">
				<div class="matchdays__day-selector-w">
					<div class="matchdays__day-selector-inner-w">
						<div
							v-for="(listEntry, i) in eventsListFiltered"
							:key="listEntry.groupKey"
							ref="daySelect"
							:data-day="listEntry.groupKey"
							class="matchdays__day-select"
							:class="{'is-active': i === 0}"
							@click="handleDaySelect(listEntry.groupKey)"
						>
							<span class="u-hide@md">{{ listEntry.titleMain.short }}</span>
							<span class="u-show@md">{{ listEntry.titleMain.short }}</span>
						</div>
					</div>
				</div>
				<div class="matchdays__list">
					<div
						v-for="listEntry in eventsListFiltered"
						:key="listEntry.groupKey"
						class="matchdays__list-inner-w"
					>
						<div
							:id="listEntry.groupKey"
							ref="scrollAnchor"
							:data-day="listEntry.groupKey"
							class="matchdays__list-scroll-anchor"
						/>
						<h3 class="matchdays__list-title">
							{{ listEntry.titleMain.long || listEntry.titleMain }}
						</h3>
						<MatchListItem
							v-for="match in listEntry.matches"
							:key="match.id"
							:dash-score-devider="true"
							v-bind="MatchListItemProps(match, { hideSeasonStageRoundInfo: true })"
						/>
					</div>
					<EmptyState
						v-if="eventsListFiltered.length === 0"
						:text="$I18n.trans('commons.no_types_found', { type: $I18n.trans_choice('commons.games', 2) })"
					/>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import MatchListItem from '@js/components/MatchCenter/MatchListItem';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import MobileFilter from '@js/components/MobileFilter';
import EmptyState from '@js/components/EmptyState';
import Icon from '@shared/components/Icon';
import { MatchListItemProps } from './MatchListItemProps';
import { getDateTime } from '@shared/utils/timeUtils';
import Select from '@shared/components/Select';
import { mapState } from 'vuex';

export default {
	name:       'MatchDaysEuf',
	components: { LoadingSpinner, MatchListItem, MobileFilter, Icon, Select, EmptyState },
	props:      {
		teams: {
			type:     Array,
			required: true,
		},
		isLoading: {
			type:    Boolean,
			default: false,
		},
		events: {
			type:    Array,
			default: null,
		},
	},
	data() {
		return {
			MatchListItemProps,
			internalEvents: this.events,
			teamFilter:     {
				type:         'select',
				id:           'team',
				label:        this.$I18n.trans_choice('commons.team', 1),
				defaultValue: 'all',
				value:        'all',
				emitEvent:    'changeTeam',
				options:      [
					{
						label: this.$I18n.trans('commons.all_types', { type: this.$I18n.trans_choice('commons.team', 2) }),
						value: 'all',
					},
					...this.teams.map(team => ({ label: team.name, value: team.id })).sort((a, b) => a.label < b.label ? -1 : 1),
				],
			},
			categoryFilter: {
				type:         'select',
				id:           'category',
				label:        this.$I18n.trans_choice('commons.division', 1),
				defaultValue: 'all',
				value:        'all',
				emitEvent:    'changeCategory',
				options:      [],
			},
			scrollObserverActive: false,
			scrollHandler:        null,
			getDateTime,
		};
	},
	computed: {
		...mapState({
			preferences: state => state.user.preferences,
		}),
		noSpoilerMode() {
			return this.preferences?.noSpoilerMode || false;
		},
		availableCategories() {
			if (!this.internalEvents || this.internalEvents.length === 0) {
				return [];
			}

			const categories = new Set();
			this.internalEvents.forEach(event => {
				if (event.participants) {
					event.participants.forEach(participant => {
						const category = participant.participatable?.category;
						if (category) {
							categories.add(category);
						}
					});
				}
			});

			return Array.from(categories).sort();
		},
		eventsList() {
			if (!this.internalEvents || this.internalEvents.length <= 0) {
				return [];
			}

			const groups = [...this.internalEvents]
				.sort((a, b) => a.startTimestamp - b.startTimestamp)
				.reduce((acc, current) => {
					const dateKey = moment.unix(current.startTimestamp).format('YYYY-MM-DD');
					const groupKey = `day_${dateKey}`;

					// Short format: "Mon 24.12" or "24.12" depending on locale
					const shortFormat = moment.unix(current.startTimestamp).format('ddd DD.MM');
					// Long format: "Monday, 24. December 2024" or similar
					const longFormat = getDateTime(current.startTimestamp, { withTime: false, withWeekdayPrefix: true, weekdayFormatLong: true, monthFormatLong: true });

					const groupTitle = {
						long:  longFormat,
						short: shortFormat,
					};

					const groupKeyExistsIndex = acc.findIndex(a => (a.groupKey === groupKey));

					if (groupKeyExistsIndex === -1) {
						acc.push({
							groupKey,
							titleMain: groupTitle,
							matches:   [current],
						});
					} else {
						acc[groupKeyExistsIndex].matches.push(current);
					}

					return acc;
				}, []);

			return groups;
		},
		eventsListFiltered() {
			let eventsListFiltered = [...this.eventsList];

			// Filter by category
			if (this.categoryFilter.value !== 'all') {
				eventsListFiltered = eventsListFiltered.map(group => {
					return {
						...group,
						matches: group.matches.filter(match => {
							return match.participants.some(p => p.participatable?.category === this.categoryFilter.value);
						}),
					};
				}).filter(group => group.matches.length > 0);
			}

			// Filter by team
			if (this.teamFilter.value !== 'all') {
				eventsListFiltered = eventsListFiltered.map(group => {
					return {
						...group,
						matches: group.matches.filter(match => match.participants.some(p => p.participatable.id === this.teamFilter.value)),
					};
				}).filter(group => group.matches.length > 0);
			}

			return eventsListFiltered;
		},
		mobileFilters() {
			return [this.categoryFilter, this.teamFilter];
		},
		mobileFilterIsFiltered() {
			return this.categoryFilter.value !== 'all' || this.teamFilter.value !== 'all';
		},
	},
	watch: {
		events(newValues) {
			this.internalEvents = newValues;
			this.updateCategoryFilter();
		},
		availableCategories() {
			this.updateCategoryFilter();
		},
	},
	mounted() {
		this.updateCategoryFilter();
		this.$nextTick(() => {
			this.initScrollObserver();
			this.goToCurrentDay();
		});
	},
	beforeDestroy() {
		this.destroyScrollObserver();
	},
	methods: {
		updateCategoryFilter() {
			const categoryOptions = [
				{
					label: this.$I18n.trans('commons.all_types', { type: this.$I18n.trans_choice('commons.division', 1) }),
					value: 'all',
				},
				...this.availableCategories.map(category => ({
					label: this.$I18n.trans(`team.category.${category}`),
					value: category,
				})).sort((a, b) => a.label < b.label ? -1 : 1),
			];

			this.categoryFilter.options = categoryOptions;
		},
		resetFilter() {
			this.teamFilter.value = 'all';
			this.categoryFilter.value = 'all';
		},
		setViewToMatchdays() {
			this.resetFilter();
		},
		goToCurrentDay() {
			const today = moment().format('YYYY-MM-DD');
			const todayKey = `day_${today}`;

			let currentDayKey = null;
			this.eventsListFiltered.forEach(eventsList => {
				if (currentDayKey) {
					return;
				}

				if (eventsList.groupKey === todayKey) {
					currentDayKey = eventsList.groupKey;
				}
			});

			// If today not found, find next upcoming day
			if (!currentDayKey) {
				this.eventsListFiltered.forEach(eventsList => {
					if (currentDayKey) {
						return;
					}

					const dayDate = moment(eventsList.groupKey.replace('day_', ''), 'YYYY-MM-DD');
					if (dayDate.isAfter(moment(), 'day')) {
						currentDayKey = eventsList.groupKey;
					}
				});
			}

			if (currentDayKey) {
				this.handleDaySelect(currentDayKey);
			}
		},
		handleDaySelect(id) {
			const element = document.querySelector(`#${id}`);
			if (element) {
				element.scrollIntoView({ block: 'start', behavior: 'smooth' });
			}
		},
		handleMobileFilterChange(select, value) {
			if (select === 'reset') {
				this.handleCategoryChange('all');
				this.handleTeamChange('all');
				return;
			}

			if (select?.id === 'category') {
				this.handleCategoryChange(value);
			}
			if (select?.id === 'team') {
				this.handleTeamChange(value);
			}
		},
		handleTeamChange(value) {
			this.teamFilter.value = value;
		},
		handleCategoryChange(value) {
			this.categoryFilter.value = value;
		},
		initScrollObserver() {
			if (this.scrollObserverActive) {
				return;
			}

			let ticking = false;
			this.scrollHandler = () => {
				if (!ticking) {
					window.requestAnimationFrame(() => {
						this.setScrollDay();
						ticking = false;
					});
					ticking = true;
				}
			};

			window.addEventListener('scroll', this.scrollHandler);
			this.scrollObserverActive = true;
		},
		destroyScrollObserver() {
			if (this.scrollHandler) {
				window.removeEventListener('scroll', this.scrollHandler);
				this.scrollHandler = null;
				this.scrollObserverActive = false;
			}
		},
		setScrollDay() {
			const scrollAnchor = this.$refs.scrollAnchor;
			if (!scrollAnchor || scrollAnchor.length === 0) {
				return;
			}

			const anchorItems = this.$refs.scrollAnchor.map(anchor => {
				return {
					el:  anchor,
					pos: anchor.getBoundingClientRect().top - 10,
				};
			});

			if (anchorItems.length === 0) {
				return;
			}

			const currentDay = anchorItems.filter(item => item.pos <= 0).reverse()[0]?.el.dataset.day || anchorItems[0].el.dataset.day;

			if (!this.$refs.daySelect) {
				return;
			}

			this.$refs.daySelect.forEach(select => {
				select.classList.remove('is-active');
				if (select.dataset.day === currentDay) {
					select.classList.add('is-active');
				}
			});
		},
		openMobileFilter() {
			this.$refs.mobileFilter.open();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.matchdays__loading-spinner {
	position: absolute;
	top: 20vh;
	left: 50%;
	transform: translate(-50%);
}

.matchdays__filter {
	margin-bottom: $sp5;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid var(--color-grey-900);

	@include size(md-up) {
		margin-bottom: $sp8;
	}
}

.matchdays__filter-select {
	display: none;

	@include size(md-up) {
		display: flex;
		gap: $sp5;
		margin-bottom: 0;

		> div { // stylelint-disable-line selector-max-type
			width: 250px;
		}
	}
}

.matchdays__mobile-filter-toggle {
	cursor: pointer;
	margin-bottom: $sp4;

	&.is-filtered {
		color: var(--color-hype-yellow);
	}

	@include size(md-up) {
		display: none;
	}
}

.matchdays__list-w {
	display: flex;
}

.matchdays__day-selector-w {
	min-width: 65px;

	@include size(md-up) {
		min-width: 140px;
	}
}

.matchdays__day-selector-inner-w {
	position: sticky;
	top: 110px;
}

.matchdays__day-select {
	color: var(--color-grey-400);
	padding: $sp2 0;
	margin-bottom: $sp3;
	cursor: pointer;

	&:focus,
	&:hover,
	&.is-active {
		color: var(--color-soft-white);

		body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
			color: var(--color-hype-yellow);
		}
	}
}

.matchdays__list {
	flex-grow: 1;

	body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
		color: var(--color-black);
	}
}

.matchdays__list-inner-w {
	margin-bottom: $sp6;
}

.matchdays__list-scroll-anchor {
	visibility: hidden;
	width: 0;
	height: 0;
	position: relative;
	top: -100px;
}

.matchdays__list-title {
	@include font(primary, book, normal, fs-150);
	position: relative;
	margin-bottom: $sp3;

	@include size(md-up) {
		@include font(primary, light, normal, fs-170);
		margin-bottom: $sp4;
	}
}

.matchdays__list-date-w {
	margin-bottom: $sp6;
}

.matchdays__list-date {
	margin-bottom: $sp2;

	@include size(md-up) {
		margin-bottom: $sp4;
	}

	@include size(sm-down) {
		font-weight: 500; // stylelint-disable-line property-disallowed-list
	}
}

</style>
